// © Microsoft Corporation. All rights reserved.

import { CardContextMenuHandledProps, CardContextMenuProps, CardContextMenuUnHandledProps } from "./CardContextMenu.props";
import { ContextMenu, ContextMenuItem } from "@microsoft/fast-components-react-msft";

import Foundation from "@microsoft/fast-components-foundation-react";
import { HandledPropsKeys } from "@msnews/core";
import { Icon } from "@msnews/icon";
import { More } from "@edge-web-ui/news-icons";
import { PopOver } from "@msnews/pop-over";
import React from "react";
import { canUseDOM } from "@msnews/isomorphic-util";
import { isNil } from "lodash-es";
import { keyCodeEscape } from "@microsoft/fast-web-utilities";

interface CardContextMenuState {
    menuOpened: boolean,
    dropDownLocation?: {
        top?: number,
        left?: number,
        offset?: number
    }
}

/**
 * The component to render the context menu
 */
class CardContextMenu extends Foundation<CardContextMenuProps, CardContextMenuUnHandledProps, CardContextMenuState> {
    /**
     * handled props for card context menu
     * @protected
     * @type { HandledPropsKeys<CardContextMenuHandledProps>}
     */
    protected handledProps: HandledPropsKeys<CardContextMenuHandledProps> = {
        managedClasses: undefined,
        menuItems: undefined,
        enabled: undefined,
        buttonLabel: undefined,
        positionOffset: undefined,
        telemetryTag: undefined,
        zIndex: undefined
    };

    /**
     * Reference to Container to set/unset focus
     * @private
     * @type { React.RefObject<HTMLDivElement>}
     */
    private containerRef: React.RefObject<HTMLDivElement>;

    /**
     * Reference to menu to set/unset focus
     * @private
     * @type { React.RefObject<HTMLDivElement>}
     */
    private menuRef: React.RefObject<HTMLDivElement>;

    /**
     * Reference to anchor button
     * @private
     * @type { React.RefObject<HTMLButtonElement>}
     */
    private readonly buttonRef: React.RefObject<HTMLButtonElement>;

    /**
     * Constructor Function for the CardContextMenu
     */
    public constructor(props: CardContextMenuHandledProps) {
        super(props);
        this.state = {
            menuOpened: false
        };
        this.containerRef = React.createRef();
        this.menuRef = React.createRef();
        this.buttonRef = React.createRef();
    }

    /**
     * Component did mount lifecycle event
     * @method
     */
    public componentDidMount(): void {
        if (canUseDOM()) {
            document.addEventListener("click", this.handleUnfocus, false);
            document.addEventListener("keydown", this.handleUnfocus, false);
        }
    }

    /**
     * Component did update lifecycle event
     * @method
     */
    public componentDidUpdate(): void {
        if (this.state.menuOpened) {
            this.menuRef.current.focus();
        }
    }

    /**
     * Component will unmount lifecycle event
     * @method
     */
    public componentWillUnmount(): void {
        if (canUseDOM()) {
            document.removeEventListener("click", this.handleUnfocus, false);
            document.removeEventListener("keydown", this.handleUnfocus, false);
        }
    }

    /**
     * Renders the component
     * @method
     * @returns {JSX.Element} The Card Context Menu JSX Element
     */
    public render(): JSX.Element {
        const { enabled, managedClasses, menuItems = [], telemetryTag } = this.props;
        if (!enabled || !menuItems.length) {
            return (null);
        }

        const { menuContainer, settingButton, settingButtonIcon } = managedClasses;

        return (
            <div {...this.unhandledProps()} ref={this.containerRef} className={menuContainer}>
                <button
                    className={settingButton}
                    onClick={(e) => this.onSettingClick(e)}
                    aria-label={this.props.buttonLabel}
                    ref={this.buttonRef}
                    {...telemetryTag}
                >
                    <More className={settingButtonIcon} />
                </button>
                {this.renderPopOver()}
            </div>
        );
    }

    /**
     * Renders the pop over menu
     * @method
     * @returns {JSX.Element} The pop over menu JSX Element
     */
    private renderPopOver(): JSX.Element {
        const { menuOpened, dropDownLocation } = this.state;
        const enabled = menuOpened && dropDownLocation && !isNil(dropDownLocation.left) && !isNil(dropDownLocation.top);
        const closeOnEscape = (e: React.KeyboardEvent) => e.keyCode === keyCodeEscape && this.closeMenu();

        return (
            <PopOver {...dropDownLocation}
                onDismiss={this.closeMenu}
                anchor={this.buttonRef.current}
                autoFlipX={true} enable={enabled}
                onKeyDown={closeOnEscape}
                zIndex={this.props.zIndex} >
                <div ref={this.menuRef}>
                    <ContextMenu className={this.props.managedClasses.contextMenu} enableAutoFocus={true}>
                        {this.renderContextMenu()}
                    </ContextMenu>
                </div>
            </PopOver>
        );
    }

    /**
     * Renders the context menu items
     * @method
     * @returns {JSX.Element} The pcontext menu items JSX Element
     */
    private renderContextMenu(): JSX.Element[] {
        const { menuItems } = this.props;
        const { contextMenuItem, contextMenuItem_contentRegion, contextMenuItem_image } = this.props.managedClasses;
        return menuItems.map(itemProps => (
            <ContextMenuItem
                className={contextMenuItem}
                {...itemProps.attr}
                {...itemProps.telemetryTag}
                key={itemProps.key}
                onInvoke={e => this.onMenuItemClick(e, itemProps.onSelect)}>
                <div aria-label={itemProps.text} className={contextMenuItem_contentRegion}>
                    {itemProps.icon && (
                        <Icon glyphType={itemProps.icon} className={contextMenuItem_image} />)}
                    {itemProps.text}
                </div>
            </ContextMenuItem>
        ));
    }

    /**
     * Handles unfocusing the Context Menu
     * @method
     */
    private handleUnfocus = (e: Event) => {
        const buttonContainerContainsNode = (node: Node) => !this.containerRef.current || this.containerRef.current.contains(node);
        // Can't put ref on the PopOver react element, so we attach to a ref div and check its immediate parent
        // Must check parent because focus goes to PopOver, which is outside menuRef (otherwise menu closes when you try tab to it)
        const menuPopoverContainsNode = (node: Node) => !this.menuRef.current || this.menuRef.current.parentNode.contains(node);

        if (e.target && !buttonContainerContainsNode(e.target as Node) && !menuPopoverContainsNode(e.target as Node)) {
            this.closeMenu();
        }
    }

    /**
     * Handles closing the menu
     * @method
     */
    private closeMenu = (): void => {
        this.setState({ menuOpened: false });
    }

    /**
     * Handles the click on the menuitems
     * @method
     */
    private onMenuItemClick(event: any, onSelectCallback: (e) => void): void {
        if (onSelectCallback) {
            onSelectCallback(event);
        }
        this.closeMenu();
    }

    /**
     * Handles the click on the settings button to show the context menu
     * @method
     */
    private onSettingClick(e: any): void {
        const posOffset = this.props.positionOffset;
        const offsetX = posOffset ? posOffset.x : 0;
        const offsetY = posOffset ? posOffset.y : 0;

        let dropDownLocation = {};
        if (e.target) {
            const { top, left, width } = e.target.getBoundingClientRect();
            dropDownLocation = { top: top + offsetY + window.scrollY, left: left + offsetX + window.scrollX, offset: width };
        }
        this.setState({ menuOpened: !this.state.menuOpened, dropDownLocation });
    }
}

export { CardContextMenu as CardContextMenu };
export * from "./CardContextMenu.props";